import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams, useLocation, useNavigate } from 'react-router-dom';
import { api, authed } from '../lib/api';
import { formatMoney } from '../lib/utils';
import { useDistributor } from '../lib/auth';
import Header from '../components/Header';
import Footer from '../components/Footer';
import HeroCarousel from '../components/HeroCarousel';
import AdVideoSection from '../components/AdVideoSection';
import CategoryPills from '../components/CategoryPills';
import ProductCard from '../components/ProductCard';
import ChooseFlavourSheet from '../components/ChooseFlavourSheet';

export default function Home() {
    const { user } = useDistributor();
    const [params, setParams] = useSearchParams();
    const location = useLocation();
    const navigate = useNavigate();
    const [categories, setCategories] = useState([]);
    const [selected, setSelected] = useState(params.get('category') || '');
    const [products, setProducts] = useState([]);
    const [heroMobile, setHeroMobile] = useState([]);
    const [heroDesktop, setHeroDesktop] = useState([]);
    const [ads, setAds] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openSheet, setOpenSheet] = useState(null); // { product, variant, flavourId? }
    const [isDesktop, setIsDesktop] = useState(typeof window !== 'undefined' && window.matchMedia('(min-width: 768px)').matches);

    // Refs / state for the smart category bar hide/show.
    const productsRef = useRef(null);
    const productsHeadingRef = useRef(null);
    const [pillsHidden, setPillsHidden] = useState(false);

    useEffect(() => {
        const mq = window.matchMedia('(min-width: 768px)');
        const handler = (e) => setIsDesktop(e.matches);
        mq.addEventListener?.('change', handler);
        return () => mq.removeEventListener?.('change', handler);
    }, []);

    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                const [cats, hm, hd, av] = await Promise.all([
                    api.get('/catalog/categories'),
                    api.get('/catalog/hero-slides?device=mobile'),
                    api.get('/catalog/hero-slides?device=desktop'),
                    api.get('/catalog/ad-videos'),
                ]);
                setCategories(cats.data);
                setHeroMobile(hm.data);
                setHeroDesktop(hd.data);
                setAds(av.data);
                if (!selected && cats.data.length) setSelected(cats.data[0].slug);
            } catch (e) { console.error(e); }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!selected) return;
        (async () => {
            try {
                const res = user
                    ? await authed('distributor', { method: 'GET', url: `/catalog/products?category=${selected}` })
                    : await api.get(`/catalog/products?category=${selected}`);
                setProducts(res.data);
            } catch (e) { console.error(e); }
            setLoading(false);
        })();
    }, [selected, user]);

    // Round 4 (P1 footer fix) — only auto-scroll when the intent was set by a
    // Footer SHOP link (via `<Link state={{ scrollToProducts: true }}>`).
    // Category-pill clicks on the sticky bar update the URL param but MUST NOT
    // cause a jump (per user feedback). We also strip the state flag after
    // consuming it so a subsequent pill click doesn't accidentally re-trigger.
    const urlCategory = params.get('category');
    useEffect(() => {
        if (!urlCategory) return;
        if (urlCategory !== selected) setSelected(urlCategory);
        const wantScroll = !!location.state?.scrollToProducts;
        if (!wantScroll) return;
        const t = setTimeout(() => {
            productsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            // Consume the intent so pill clicks after the initial scroll do not re-scroll.
            navigate(location.pathname + location.search, { replace: true, state: {} });
        }, 60);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [urlCategory, location.state?.scrollToProducts]);

    const catMap = useMemo(() => Object.fromEntries(categories.map(c => [c.slug, c.name])), [categories]);
    const currentCategoryName = catMap[selected] || '';

    // Round 4 — rows are NO LONGER live-filtered by query. Search is Enter-triggered
    // and only performs discrete actions (jump to category / open a variant picker).
    const rows = useMemo(() => {
        const rowsAll = [];
        for (const p of products) {
            for (const v of p.variants || []) {
                rowsAll.push({ product: p, variant: v });
            }
        }
        return rowsAll;
    }, [products]);

    // Live search (Item 2): the header sends us a specific hit
    // {product_id, variant_id, flavour_id}. We look it up and open the popup
    // directly, no variant picker.
    const openSearchHit = async ({ product_id, variant_id, flavour_id }) => {
        // Try to find the product across all currently-loaded categories.
        let cats = categories;
        if (!cats.length) {
            try { const r = await api.get('/catalog/categories'); cats = r.data; } catch { /* ignore */ }
        }
        // Fast path: if the product is in the currently selected category list.
        let product = products.find(p => p.id === product_id);
        // Otherwise fetch each category and find it.
        if (!product) {
            for (const c of cats) {
                try {
                    const r = user
                        ? await authed('distributor', { method: 'GET', url: `/catalog/products?category=${c.slug}` })
                        : await api.get(`/catalog/products?category=${c.slug}`);
                    const hit = (r.data || []).find(p => p.id === product_id);
                    if (hit) {
                        product = hit;
                        // Also switch to that category so the underlying grid is coherent.
                        if (c.slug !== selected) {
                            setSelected(c.slug);
                            setParams({ category: c.slug });
                        }
                        break;
                    }
                } catch { /* try next */ }
            }
        }
        if (!product) return;
        const variant = (product.variants || []).find(v => v.id === variant_id);
        if (!variant) return;
        setOpenSheet({ product, variant, flavourId: flavour_id });
    };

    // React to search hits routed from other pages via navigation state.
    useEffect(() => {
        const hit = location.state?.openSearchHit;
        if (!hit) return;
        openSearchHit(hit);
        // Clear the state so the popup does not re-open on subsequent renders.
        navigate(location.pathname + location.search, { replace: true, state: {} });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.state?.openSearchHit]);

    // Batch B2 Bug-Fix Pass — smart category-bar hide/show based on scroll direction.
    //
    // Requirements:
    //  · Only engage the hide behaviour once the user has scrolled past the ad-video
    //    area (i.e. the products grid is in view). While hero / ad video are visible,
    //    the bar stays shown.
    //  · Any upward scroll — however small — must immediately reveal the bar so the
    //    user can pick a category without scrolling back to the top. This fixes the
    //    reported bug: "scrolling up from the bottom didn't bring the bar back".
    //  · Reveal automatically when the user has reached (or is very close to) the
    //    bottom of the page, so the bar is available for the "select a category
    //    from the bottom" flow.
    //  · Use rAF-throttled scroll listener + refs to avoid re-render churn.
    const pillsHiddenRef = useRef(false);
    useEffect(() => {
        let lastY = window.scrollY || 0;
        let ticking = false;

        const process = () => {
            const y = window.scrollY || 0;
            const productsTop = productsRef.current?.getBoundingClientRect().top ?? 9999;
            const inProductsZone = productsTop <= 8;
            const docH = document.documentElement.scrollHeight;
            const winH = window.innerHeight;
            const atBottom = y + winH >= docH - 8;

            if (!inProductsZone || atBottom) {
                // Always show while hero/ad-video is in view or when user has reached
                // the bottom of the page.
                if (pillsHiddenRef.current) {
                    pillsHiddenRef.current = false;
                    setPillsHidden(false);
                }
                lastY = y;
                ticking = false;
                return;
            }
            const dy = y - lastY;
            // Scroll UP: reveal immediately (no dead-zone) so the user can switch
            // categories the moment they change direction.
            if (dy < 0 && pillsHiddenRef.current) {
                pillsHiddenRef.current = false;
                setPillsHidden(false);
            } else if (dy > 6 && !pillsHiddenRef.current) {
                // Only hide when scroll-down movement is meaningful, avoids jitter.
                pillsHiddenRef.current = true;
                setPillsHidden(true);
            }
            lastY = y;
            ticking = false;
        };

        const onScroll = () => {
            if (!ticking) {
                ticking = true;
                window.requestAnimationFrame(process);
            }
        };
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    return (
        <div className="App min-h-screen bg-sdf-ivory">
            <Header onOpenSearchHit={openSearchHit} />
            <main>
                <HeroCarousel slides={isDesktop ? heroDesktop : heroMobile} device={isDesktop ? 'desktop' : 'mobile'} />
                <AdVideoSection videos={ads} />
                <CategoryPills
                    categories={categories}
                    selected={selected}
                    onSelect={(slug) => {
                        setSelected(slug);
                        setParams({ category: slug });
                        // Batch B2 Bug-Fix — after switching category, ensure the
                        // "<CATEGORY> BY SIZES" heading is visible below the sticky
                        // bar. If it is already visible, do NOT scroll (preserves the
                        // "no-jump" spec from Batch A). If it's hidden behind header
                        // + pills, smoothly scroll just enough to reveal it.
                        setTimeout(() => {
                            const heading = productsHeadingRef.current;
                            if (!heading) return;
                            // Approximate the sticky-blocker height: 52px site header
                            // + pills bar (measured live). Fallback 60px.
                            const pillsEl = document.querySelector('[data-testid="category-pills-bar"]');
                            const pillsH = pillsEl?.getBoundingClientRect().height ?? 60;
                            const requiredTop = 52 + pillsH + 8;   // 8px breathing space
                            const rect = heading.getBoundingClientRect();
                            if (rect.top < requiredTop - 1) {
                                const y = window.scrollY + rect.top - requiredTop;
                                window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' });
                            }
                        }, 30);
                    }}
                    hidden={pillsHidden}
                />
                <div ref={productsRef} className="max-w-6xl mx-auto px-3 sm:px-4 pt-4 pb-8" data-testid="products-section">
                    <h1
                        ref={productsHeadingRef}
                        className="text-2xl sm:text-3xl font-extrabold text-sdf-text uppercase tracking-tight scroll-mt-[130px]"
                        data-testid="products-heading"
                    >
                        {currentCategoryName} by Sizes
                    </h1>
                    <p className="text-xs text-sdf-text-2 mt-1">Pick a weight first, then choose from all flavours available in that pack size.</p>
                    <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                        {rows.map(({ product, variant }) => (
                            <ProductCard
                                key={`${product.id}-${variant.id}`}
                                product={product}
                                variant={variant}
                                categoryName={currentCategoryName}
                                onChoose={setOpenSheet}
                            />
                        ))}
                    </div>
                    {rows.length === 0 && !loading && (
                        <div className="py-16 text-center">
                            <div className="text-lg font-bold text-sdf-text">No products in {currentCategoryName} yet.</div>
                            <div className="text-sm text-sdf-text-2 mt-1">Products will appear here as soon as the admin adds them from the Admin Dashboard.</div>
                        </div>
                    )}
                </div>
                {/* Footer is INSIDE <main> so the sticky Category Selection Bar
                    (whose containing block is <main>) can remain pinned at the
                    top of the viewport all the way down to the footer. Moving
                    the footer outside <main> would clip the sticky bar as soon
                    as <main>'s bottom edge crossed the viewport top. */}
                <Footer />
            </main>
            <ChooseFlavourSheet
                open={!!openSheet}
                onClose={() => setOpenSheet(null)}
                product={openSheet?.product}
                initialVariantId={openSheet?.variant?.id}
                initialFlavourId={openSheet?.flavourId}
            />
        </div>
    );
}
